import { Box, Menu, MenuButton, MenuList, MenuItem, IconButton, MenuDivider, useDisclosure, Switch, Flex, Text, Heading, Image, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Button, FormControl, FormLabel, Input, useToast, Icon } from '@chakra-ui/react';
import { HamburgerIcon, TimeIcon, DownloadIcon, ViewIcon, AddIcon, MoonIcon, SunIcon } from '@chakra-ui/icons';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { MdFullscreen } from 'react-icons/md';
import { getTranslations } from '../translations';
import AboutModal from './AboutModal';
import InstallModal from './InstallModal';
import useInstallPrompt from '../hooks/useInstallPrompt';
import logo2_700 from '../../logo2-700.png';
import { useState, useEffect } from 'react';

const Header = ({ onNavigate, wakeLockEnabled, setWakeLockEnabled, isDarkMode, setIsDarkMode, startupTime, setStartupTime, soundEnabled, setSoundEnabled, isFullscreen }) => {
    const t = getTranslations();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isInstallModalOpen, onOpen: onInstallModalOpen, onClose: onInstallModalClose } = useDisclosure();
    const { isOpen: isStartupTimeModalOpen, onOpen: onStartupTimeModalOpen, onClose: onStartupTimeModalClose } = useDisclosure();
    const { platform, isInstallable, handleInstall, canShowPrompt } = useInstallPrompt();
    const [tempStartupTime, setTempStartupTime] = useState(startupTime);

    // Sincronizza tempStartupTime quando startupTime cambia o quando si apre il modal
    useEffect(() => {
        if (isStartupTimeModalOpen) {
            setTempStartupTime(startupTime);
        }
    }, [isStartupTimeModalOpen, startupTime]);

    const handleInstallClick = async () => {
        if (canShowPrompt) {
            // Mostra il prompt nativo (Chrome/Edge/Android)
            const installed = await handleInstall();
            if (!installed) {
                // Se l'utente ha rifiutato o c'è stato un errore, mostra comunque le istruzioni
                onInstallModalOpen();
            }
        } else {
            // Mostra le istruzioni manuali (iOS/Safari o browser non supportati)
            onInstallModalOpen();
        }
    };

    const handleStartupTimeClick = () => {
        onStartupTimeModalOpen();
    };

    const handleSaveStartupTime = () => {
        const value = Math.max(0, Math.min(9999, parseInt(tempStartupTime, 10) || 5));
        setStartupTime(value);
        setTempStartupTime(value);
        onStartupTimeModalClose();
        toast({
            title: t.startupTimeDialogSave || 'Salvato',
            description: `${t.startupTimeMenu || 'Tempo di preparazione'}: ${value}s`,
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    return (
        <Box w="100%" mb={4} position="relative" display="flex" alignItems="center">
            <Menu closeOnSelect={false}>
                <MenuButton
                    as={IconButton}
                    aria-label="Menu"
                    icon={<HamburgerIcon boxSize="1.5em" />}
                    variant="outline"
                    colorScheme="gray"
                    bg={isDarkMode ? 'gray.700' : 'white'}
                    color={isDarkMode ? 'white' : 'gray.800'}
                    _hover={{ bg: isDarkMode ? 'gray.600' : 'gray.100' }}
                    _active={{ bg: isDarkMode ? 'gray.500' : 'gray.200' }}
                    boxShadow="base"
                    size="lg"
                    minW="60px"
                    h="60px"
                    position="relative"
                    zIndex={1}
                />
                <MenuList zIndex={20} bg={isDarkMode ? 'gray.800' : 'white'} borderColor={isDarkMode ? 'gray.700' : 'gray.200'}>
                    <MenuItem icon={<TimeIcon />} onClick={() => onNavigate('timer')}>
                        Timer
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem>
                        <Flex justify="space-between" align="center" w="100%" onClick={(e) => e.stopPropagation()}>
                            <Text fontSize="md" mr={4}>{t.keepScreenOn}</Text>
                            <Switch
                                colorScheme="brand"
                                isChecked={wakeLockEnabled}
                                onChange={(e) => setWakeLockEnabled(e.target.checked)}
                            />
                        </Flex>
                    </MenuItem>
                    <MenuItem>
                        <Flex justify="space-between" align="center" w="100%" onClick={(e) => e.stopPropagation()}>
                            <Flex align="center">
                                {isDarkMode ? <MoonIcon mr={2} /> : <SunIcon mr={2} />}
                                <Text fontSize="md" mr={4}>{isDarkMode ? 'Scuro' : 'Chiaro'}</Text>
                            </Flex>
                            <Switch
                                colorScheme="brand"
                                isChecked={isDarkMode}
                                onChange={(e) => setIsDarkMode(e.target.checked)}
                            />
                        </Flex>
                    </MenuItem>
                    <MenuItem>
                        <Flex justify="space-between" align="center" w="100%" onClick={(e) => e.stopPropagation()}>
                            <Flex align="center">
                                <Icon as={soundEnabled ? FaVolumeUp : FaVolumeMute} mr={2} />
                                <Text fontSize="md" mr={4}>{t.sound || 'Suono'}</Text>
                            </Flex>
                            <Switch
                                colorScheme="brand"
                                isChecked={soundEnabled}
                                onChange={(e) => setSoundEnabled(e.target.checked)}
                            />
                        </Flex>
                    </MenuItem>
                    <MenuItem icon={<TimeIcon />} onClick={handleStartupTimeClick}>
                        {t.startupTimeMenu || 'Tempo di preparazione'} ({startupTime}s)
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<ViewIcon />} onClick={() => onNavigate('gallery')}>
                        Visualizza Icone
                    </MenuItem>
                    <MenuItem icon={<DownloadIcon />} onClick={() => onNavigate('download')}>
                        Download Icone
                    </MenuItem>
                    {isInstallable && (
                        <>
                            <MenuDivider />
                            <MenuItem icon={<AddIcon />} onClick={handleInstallClick}>
                                {t.installApp}
                            </MenuItem>
                        </>
                    )}
                    <MenuDivider />
                    <MenuItem onClick={onOpen}>{t.about || 'About'}</MenuItem>
                </MenuList>
            </Menu>
            <Image
                src={logo2_700}
                alt="Workout Timer"
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
                maxH={{ base: "78px", md: "80px", lg: "100px" }}
                maxW={{ base: "260px", md: "300px", lg: "400px" }}
                height="auto"
                width="auto"
                objectFit="contain"
                pointerEvents="none"
                sx={{
                    transition: 'all 0.3s ease'
                }}
            />
            {/* Indicatore fullscreen a destra del logo */}
            {isFullscreen && (
                <Box
                    position="absolute"
                    left="calc(50% + 150px)"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="10"
                    bg={isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)'}
                    borderRadius="md"
                    p={2}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    boxShadow="md"
                    sx={{
                        '@media (max-width: 768px)': {
                            left: 'calc(50% + 130px)',
                        }
                    }}
                >
                    <Icon as={MdFullscreen} boxSize="1.2rem" color={isDarkMode ? "teal.300" : "teal.600"} />
                    <Text fontSize="xs" fontWeight="bold" color={isDarkMode ? "teal.300" : "teal.600"}>
                        {t.fullscreenActive || 'Fullscreen'}
                    </Text>
                </Box>
            )}
            <AboutModal isOpen={isOpen} onClose={onClose} />
            <InstallModal isOpen={isInstallModalOpen} onClose={onInstallModalClose} platform={platform} />
            
            <Modal isOpen={isStartupTimeModalOpen} onClose={onStartupTimeModalClose} isCentered>
                <ModalOverlay />
                <ModalContent bg={isDarkMode ? 'gray.800' : 'white'} borderColor={isDarkMode ? 'gray.700' : 'gray.200'}>
                    <ModalHeader color={isDarkMode ? 'white' : 'gray.800'}>
                        {t.startupTimeDialogTitle || 'Tempo di Preparazione'}
                    </ModalHeader>
                    <ModalCloseButton color={isDarkMode ? 'white' : 'gray.800'} />
                    <ModalBody>
                        <FormControl>
                            <FormLabel color={isDarkMode ? 'gray.200' : 'gray.700'} mb={3}>
                                {t.startupTimeDialogLabel || 'Imposta il tempo di countdown (secondi)'}
                            </FormLabel>
                            <Input
                                type="number"
                                value={tempStartupTime}
                                onChange={(e) => setTempStartupTime(Math.max(0, Math.min(9999, parseInt(e.target.value, 10) || 0)))}
                                min={0}
                                max={9999}
                                bg={isDarkMode ? 'gray.700' : 'white'}
                                color={isDarkMode ? 'white' : 'gray.800'}
                                borderColor={isDarkMode ? 'gray.600' : 'gray.300'}
                                _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 1px teal.500' }}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={onStartupTimeModalClose}
                            color={isDarkMode ? 'gray.300' : 'gray.600'}
                        >
                            {t.startupTimeDialogCancel || 'Annulla'}
                        </Button>
                        <Button colorScheme="teal" onClick={handleSaveStartupTime}>
                            {t.startupTimeDialogSave || 'Salva'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Header;

